/**
 * PostHog app — the storefront half of the contract, zod-free on purpose: the
 * storefront imports it from every page that can identify a guest, and the
 * zod runtime is kept out of the browser bundle (see
 * `apps/traveler-web/.../_lead-form/lead-form-state.ts`). The operator half
 * (the saved settings) is the zod schema in `./posthog.ts`.
 */

export const POSTHOG_REGIONS = ["us", "eu"] as const;
export type PosthogRegion = (typeof POSTHOG_REGIONS)[number];

/** Ingest host per region; the operator picks a region, never a URL. */
export const POSTHOG_API_HOSTS: Readonly<Record<PosthogRegion, string>> = {
  us: "https://us.i.posthog.com",
  eu: "https://eu.i.posthog.com",
};

/** The window key the app's inline script writes the config to. */
export const POSTHOG_STOREFRONT_CONFIG_GLOBAL = "__tcAgencyPosthog";

export const POSTHOG_PROJECT_KEY_PATTERN = /^phc_[A-Za-z0-9]+$/;

export interface PosthogStorefrontConfig {
  apiKey: string;
  apiHost: string;
  sessionReplay: boolean;
  autocapture: boolean;
  identifyVisitors: boolean;
}

const HOSTS = new Set<string>(Object.values(POSTHOG_API_HOSTS));

/** Null for anything but a well-formed config — any script on the page can
 *  write the window key, so the storefront never trusts its shape. */
export function parsePosthogStorefrontConfig(
  raw: unknown,
): PosthogStorefrontConfig | null {
  if (typeof raw !== "object" || raw === null) return null;
  const c = raw as Record<string, unknown>;
  const ok =
    typeof c.apiKey === "string" &&
    POSTHOG_PROJECT_KEY_PATTERN.test(c.apiKey) &&
    typeof c.apiHost === "string" &&
    HOSTS.has(c.apiHost) &&
    typeof c.sessionReplay === "boolean" &&
    typeof c.autocapture === "boolean" &&
    typeof c.identifyVisitors === "boolean";
  return ok ? (c as unknown as PosthogStorefrontConfig) : null;
}

export interface PosthogIdentity {
  /** Trimmed and lowercased: it is the PostHog distinct id, so sign-in,
   *  checkout and form identities join into one person. */
  email: string;
  name?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function toPosthogIdentity(
  email: string,
  name?: string | null,
): PosthogIdentity | null {
  const normalized = email.trim().toLowerCase();
  if (normalized.length > 254 || !EMAIL.test(normalized)) return null;
  const trimmedName = name?.trim().slice(0, 120);
  return trimmedName
    ? { email: normalized, name: trimmedName }
    : { email: normalized };
}

export const POSTHOG_STOREFRONT_EVENTS = {
  signedIn: "signed_in",
  /** The "Ask a question" form was sent successfully. */
  questionSent: "question_sent",
  /** Properties: `{ bookingRef }`. */
  bookingCompleted: "booking_completed",
  invoiceOpened: "invoice_opened",
} as const;
